import { useContext, useMemo } from 'react'
import type { CSSProperties } from 'react'

import { cx } from '../../atoms/cx.ts'
import { hasSkin } from '../../tokens/skin.ts'
import { ChamberFigure, type ChamberFigureProps } from '../ChamberFigure.tsx'
import type { ChamberStageProgress } from '../chamberStageLook.ts'
import { ChamberStageFigure } from '../ChamberStageFigure.tsx'

import { VIEW_H, VIEW_W } from './chamberGeometry.ts'
import { ChamberStepContext, StainPipetteWellContext } from './chamberRegistry.ts'
import type { ChamberDetail, ChamberPhase } from './chamberScene.ts'

/**
 * 챔버 한 개 — **늘 2D 그림이다.**
 *
 * 🔴 **3D 는 걷혔다**(2026-10-01 CTCells 회의 「3D 장비 뷰 → 2D 로 변경」 · GPU 부담 · 정보 전달 효과 낮음).
 *    캔버스 · 등록소 · 회전 · three 의존성은 같은 날 리포에서 지웠다. 되살리려면 git 이력에서 꺼낸다.
 * 🔴 **통합 콘솔(스킨 있음)은 하이픽 챔버 선 그림**(`ChamberStageFigure`)이고, 그 밖 앱(장비 관점 · 모듈 패드)은
 *    종전 그림(`ChamberFigure` · 이름이 사각형 안)이다.
 * 🔴 **상자 크기는 종전과 같은 비율**(`VIEW_W`×`VIEW_H`)이다. 카드의 챔버 자리를 안 흔든다.
 */
export interface ChamberViewProps extends ChamberFigureProps {
  /**
   * 공정 자리(단계 · 진행/완료). 🔴 **넘기면 우물 안이 그 단계의 모습**이 된다
   * (eric 2026-09-23). 안 넘기면 상태 6종의 형태만 그린다.
   */
  phase?: ChamberPhase | undefined
  /**
   * 그 웰의 분주 기록(웰 근거일 때만 · `ChamberDetail.well`). 🔴 **넘기는 쪽이 서버 사실만 넘긴다** — 스포이드는
   * `latest` 인 웰에만 선다(`chamberTool`).
   */
  wellProgress?: ChamberDetail['well']
  /**
   * 챔버 진행 4종(`chamberProgress().status` — 준비 중 · 진행 · 완료 · 실패). 🔴 **통합 콘솔 2D 의 테**가 읽는다
   * (`ChamberStageFigure`).
   */
  progress?: ChamberStageProgress | undefined
}

export function ChamberView({ well, state, phase, wellProgress, progress }: ChamberViewProps) {
  const step = useContext(ChamberStepContext)
  /* 염색기 피펫이 지금 이 웰에 있나 — 컨테이너가 서버 사실로 고른 한 칸(`StainPipetteWellContext`) */
  const pipetteHere = useContext(StainPipetteWellContext) === well
  const status = wellProgress?.status
  const latest = wellProgress?.latest === true
  const stepStage = step?.stage
  const stepName = step?.step
  const detail = useMemo<ChamberDetail | undefined>(() => {
    const out: ChamberDetail = {
      ...(stepStage === undefined || stepName === undefined
        ? {}
        : { step: { stage: stepStage, step: stepName } }),
      ...(status === undefined ? {} : { well: { status, ...(latest ? { latest } : {}) } }),
      ...(pipetteHere ? { pipetteHere: true } : {}),
    }
    return out.step === undefined && out.well === undefined && out.pipetteHere !== true
      ? undefined
      : out
  }, [stepStage, stepName, status, latest, pipetteHere])

  /*
   * 🔴 **통합 콘솔의 2D 는 웰 이름을 그림 «밖»에 둔다**(eric 2026-09-29 「사각형 안에 웰 번호가 있는데
   *    이걸 바깥으로 빼줘」). 카드 · 서랍이 이름 자리 규칙을 `.ch3d__well` 에 건다 — ⚠ 클래스 이름의 `3d` 는
   *    3D 시절의 흔적이다. 이름을 바꾸면 카드 · 서랍 CSS 와 시험이 같이 바뀌어야 해서 그대로 두었다.
   * 🔴 **콘솔 2D 의 그림은 하이픽 챔버 선 그림이다**(`ChamberStageFigure` · eric 2026-09-29 「3d 처럼 스텝에 따라서
   *    상태를 바뀌게」 · 「하이픽 챔버 느낌 나게」). 장면 모델(`chamberScene` · `chamberTool`)을 읽는다.
   * 🔴 **콘솔 스킨에서만이다.** 장비 관점 콘솔 · 모듈 패드는 오늘 그림(`ChamberFigure` · 이름이 사각형 안)을 그대로 둔다.
   */
  if (!hasSkin()) return <ChamberFigure state={state} well={well} />
  return (
    <span
      className={cx('ch2d', `ch2d--${state}`)}
      style={{ '--ch3d-w': VIEW_W, '--ch3d-h': VIEW_H } as CSSProperties}
    >
      <ChamberStageFigure detail={detail} phase={phase} progress={progress} state={state} />
      <span className="ch3d__well mono">{well}</span>
    </span>
  )
}
